/**
 * Supabase Workflow Descriptions API Service
 *
 * Handles CRUD operations for Make.com workflow descriptions
 */

import { supabase } from './client';

export interface WorkflowDescription {
  id: string;
  scenarioId: number;
  title: string | null;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

interface DbWorkflowDescription {
  id: string;
  scenario_id: number;
  title: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
}

const mapToWorkflowDescription = (row: DbWorkflowDescription): WorkflowDescription => ({
  id: row.id,
  scenarioId: row.scenario_id,
  title: row.title,
  description: row.description,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

class WorkflowDescriptionsApi {
  /**
   * Get workflow description by scenario ID
   */
  async getByScenarioId(scenarioId: number): Promise<WorkflowDescription | null> {
    const { data, error } = await supabase
      .from('workflow_descriptions')
      .select('*')
      .eq('scenario_id', scenarioId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned - not an error, just doesn't exist yet
        return null;
      }
      console.error('Failed to fetch workflow description:', error);
      return null;
    }

    return mapToWorkflowDescription(data);
  }

  /**
   * Create or update workflow description
   */
  async upsert(scenarioId: number, title: string, description: string): Promise<WorkflowDescription | null> {
    const { data, error } = await supabase
      .from('workflow_descriptions')
      .upsert(
        {
          scenario_id: scenarioId,
          title,
          description,
          updated_at: new Date().toISOString(),
        },
        {
          onConflict: 'scenario_id',
        }
      )
      .select()
      .single();

    if (error) {
      console.error('Failed to upsert workflow description:', error);
      throw new Error(`Failed to save workflow description: ${error.message}`);
    }

    return mapToWorkflowDescription(data);
  }

  /**
   * Delete workflow description
   */
  async delete(scenarioId: number): Promise<boolean> {
    const { error } = await supabase
      .from('workflow_descriptions')
      .delete()
      .eq('scenario_id', scenarioId);

    if (error) {
      console.error('Failed to delete workflow description:', error);
      return false;
    }

    return true;
  }
}

export const workflowDescriptionsApi = new WorkflowDescriptionsApi();
